/** Textes « caisse » — le français fait foi. */
export const caisse = {
  etatsJour: { loue: 'louée', reserve: 'réservée', entretien: 'au garage', libre: 'libre' } as Record<string, string>,
  /** Jours de la semaine abrégés, dimanche d'abord (Date.getDay). */
  jours: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
  modes: {
    especes: 'Espèces', cheque: 'Chèque', virement: 'Virement', carte: 'Carte', traite: 'Traite',
  } as Record<string, string>,
  dossierIntrouvable: 'Ce dossier est introuvable — l\'encaissement ne sera rattaché à rien.',

  titre: 'Caisse',
  sousTitre: (devise: string) => `Un tiroir par devise · total consolidé en ${devise}`,
  vueJour: 'Jour',
  vueMois: 'Mois',
  encaisser: 'Encaisser',
  operationChange: 'Opération de change',
  tauxDuJour: 'Taux du jour',
  reserveGerants: 'Réservé aux gérants',
  cloturer: 'Clôturer la caisse',

  selecteur: {
    precedent: 'Mois précédent',
    suivant: 'Mois suivant',
    choisir: 'Choisir le mois',
    enCours: ' — en cours',
    aVenir: ' — à venir',
    revenir: 'Revenir au mois en cours',
  },

  /** Le bandeau d'un mois à venir : les parties en gras sont séparées. */
  moisAVenir: {
    titre: 'Mois à venir — ce qui est déjà réservé',
    gras1: (mois: string) => `Rien n'a encore été encaissé ni dépensé en ${mois}.`,
    texte1: ' Les montants ci-dessous qui ne sont pas à zéro ne viennent pas de ce mois : le ',
    gras2: 'solde de caisse',
    texte2: ' est ce que le tiroir contient aujourd\'hui, ',
    gras3: '« ce qu\'on nous doit »',
    texte3: ' est l\'impayé à ce jour, et ',
    gras4: '« prévu ce mois »',
    texte4: (mois: string) => ` est ce que les locations déjà réservées rapporteront — rien de tout cela n'est de l'argent reçu en ${mois}. Une location de ce mois déjà payée d'avance est comptée dans la caisse du mois où l'argent est entré, et la voiture concernée l'indique.`,
  },

  bilan: {
    fonds: 'Fonds de caisse posé',
    fondsDetail: 'ce que vous avez mis dans la caisse ce mois-ci',
    encaisse: 'Encaissé',
    depense: 'Dépensé',
    fondsConsomme: (pct: number) => `${pct} % du fonds consommé`,
    soldeAujourdhui: 'Solde de caisse aujourd\'hui',
    solde: 'Solde de caisse',
    maintenantAvant: 'ce que le tiroir contient ',
    maintenantGras: 'maintenant',
    maintenantApres: (mois: string) => ` — rien n'a encore bougé en ${mois}`,
    auPremier: 'au 1er ',
    verseBanque: '− versé en banque ',
  },

  quiAFaitQuoi: 'Qui a fait quoi ce mois-ci',
  colonnesPersonnes: { personne: 'Personne', operations: 'Opérations', encaisse: 'Encaissé', depense: 'Dépensé' },
  noteNominative: 'Chaque ligne est nominative et vient du journal : personne ne peut dépenser sans que son nom reste. Le fonds est la somme des apports du mois.',

  rendement: {
    titre: (mois: string) => `Ce que chaque voiture a rapporté — ${mois}`,
    legende: { louee: 'Louée', reservee: 'Réservée', entretien: 'Entretien', libre: 'Libre' },
    prevu: 'Prévu ce mois',
    genere: 'Généré ce mois',
    prevuDetail: 'locations déjà réservées — rien n\'est encaissé',
    genereDetail: (actives: number, total: number) => `${actives} voiture(s) ont travaillé sur ${total}`,
    encaissePourVoitures: 'Encaissé pour ces voitures',
    dontAvance: (m: string) => `dont ${m} d'avance pour les mois suivants`,
    dontBanque: (m: string) => `dont ${m} à la banque`,
    dejaAvant: (m: string) => `${m} de ce mois déjà encaissés avant`,
    encaisseDetail: 'l\'argent des locations reçu ce mois-ci',
    reservePlusTard: 'Réservé pour plus tard',
    reservePlusTardDetail: 'déjà promis, au-delà de ce mois',
    immobiles: 'Voitures immobiles',
    immobilesDetail: 'aucun jour loué ni réservé',
    parJour: (m: string) => `${m} / jour`,
    joursLoues: (j: number) => `${j} j loué${j > 1 ? 's' : ''}`,
    joursReserves: (j: number) => ` · ${j} réservé(s)`,
    joursGarage: (j: number) => ` · ${j} au garage`,
    locations: (n: number) => `${n} location(s)`,
    reserveApres: (j: number) => `Déjà réservée ${j} jour(s) après ce mois — `,
    pasSortie: 'Pas sortie de tout le mois.',
    encaisseMois: 'Encaissé ce mois',
  },

  supplements: {
    titre: (m: string) => `Suppléments facturés — ${m}`,
    vide: 'Aucun supplément ce mois-ci',
    videTexte: 'Retards, carburant manquant, kilomètres au-delà du forfait et lavages apparaîtront ici, avec le calcul qui les justifie.',
    colonnes: { supplement: 'Supplément', calcul: 'Calcul', voiture: 'Voiture', montant: 'Montant' },
  },
  cautions: {
    titre: (m: string) => `Cautions — ${m} détenus`,
    vide: 'Aucun mouvement de caution',
    colonnes: { mouvement: 'Mouvement', detail: 'Détail', par: 'Par', montant: 'Montant' },
    note: 'Une caution n\'est pas un gain : c\'est l\'argent d\'un client, gardé. Elle est retirée de la trésorerie réellement disponible.',
  },

  ouArgent: {
    titre: (mois: string) => `Où est passé l'argent — ${mois}`,
    sorti: 'Sorti de la caisse',
    sortiDetail: 'vers la banque, les dépenses, ou dépensé directement',
    doitAujourdhui: 'Ce qu\'on nous doit aujourd\'hui',
    doit: 'Ce qu\'on nous doit',
    doitDetail: (n: number, aVenir: boolean) => `${n} dossier(s) non soldé(s)` + (aVenir ? ' · tous mois confondus' : ''),
    avances: 'Avances encaissées',
    avancesDetail: 'déjà payé, pas encore livré',
    sorties: 'Sorties de la caisse principale',
    rienSorti: 'Rien n\'est sorti de la caisse ce mois-ci.',
    colonnesSorties: { date: 'Date', motif: 'Motif', vers: 'Vers', par: 'Par', montant: 'Montant' },
    personne: 'Personne ne vous doit rien.',
    colonnesDettes: { client: 'Client', voiture: 'Voiture', dossier: 'Dossier', resteDu: 'Reste dû' },
    aCredit: ' · à crédit',
    parTranches: ' · par tranches',
    avancesTitre: 'Avances : encaissé pour des locations pas encore livrées',
    aucuneAvance: 'Aucune avance en cours.',
    colonnesAvances: {
      dossier: 'Dossier', voiture: 'Voiture', client: 'Client', periode: 'Période',
      encaisse: 'Encaissé', nonLivre: 'Pas encore livré',
    },
    avancesNote: 'Cet argent est dans votre caisse mais il n\'est pas gagné : la voiture doit encore sortir. C\'est la part du solde qu\'il ne faut pas engager.',
  },

  jour: {
    totalConsolide: 'Total consolidé',
    dont: 'dont ',
    futures: ' de prestations futures déjà encaissées et ',
    cautions: ' de cautions — ',
    nonDisponibles: 'non disponibles',
    fin: '.',
    disponible: 'Trésorerie réellement disponible',
    toutesPoches: 'toutes poches ',
    moinsFutures: '− futures ',
    moinsCautions: '− cautions ',
    mouvements: 'Mouvements',
    aucunMouvement: 'Aucun mouvement ce jour-là',
    colonnes: {
      heure: 'Heure', type: 'Type', libelle: 'Libellé', mode: 'Mode', devise: 'Devise',
      montant: 'Montant', taux: 'Taux', contrevaleur: 'Contrevaleur', agent: 'Agent',
    },
    resultatChange: (gain: boolean, m: string) => `${gain ? 'gain' : 'perte'} de change ${m}`,
    versBanque: '→ banque',
  },
  noteAgent: 'La clôture de caisse est réservée au responsable. Vos encaissements sont définitifs dès l\'enregistrement.',

  encaissement: {
    confirmerDoublon: "Oui, c'est un second versement",
    libelleContrat: (numero: string, client: string) => `Solde location ${numero} — ${client}`,
    libelleDefaut: 'Encaissement location',
    rattache: (numero: string) => `Encaissement rattaché au contrat ${numero}.`,
    enregistre: 'Encaissement enregistré.',
    erreur: 'Erreur',
    titreContrat: (numero: string) => `Encaisser — contrat ${numero}`,
    titre: 'Encaisser',
    resteDu: (m: string) => `Reste dû : ${m}`,
    resteDuTexte: 'Cet encaissement sera rattaché au dossier : le solde du contrat et la trésorerie disponible se mettent à jour.',
    nonRattache: 'Encaissement non rattaché',
    nonRattacheTexte: 'Aucun dossier n\'est sélectionné : ce montant entrera en caisse sans solder de contrat. Pour solder une location, partez du dossier ou de la liste « Soldes à encaisser ».',
    libelle: 'Libellé',
    sansTaux: (devises: string) => `Aucun taux du jour pour ${devises}`,
    sansTauxAvant: (devise: string) => `Sans taux, cet encaissement ne peut pas être converti en ${devise} et sera refusé. Un gérant le saisit dans `,
    sansTauxGras: 'Caisse › Taux du jour',
    sansTauxApres: '.',
    montant: 'Montant',
    devise: 'Devise',
    payeEn: 'Payé en',
    retirer: 'retirer',
    conversion: (n: string, devise: string, m: string, taux: string) => `${n} ${devise} ≈ ${m} (taux ${taux})`,
    ajouterDevise: '+ Ajouter une devise (paiement mixte)',
    banqueTitre: 'Une partie va à la banque, pas à la caisse',
    banqueTexte: (liste: string) => `${liste} — la caisse ne contient que ce qui se compte à la main. Le reste du dossier se solde pareil : c'est le total qui compte.`,
    totalEn: (devise: string) => `Total en ${devise}`,
  },

  change: {
    enregistre: 'Opération de change enregistrée.',
    sortie: 'Sortie de caisse',
    entree: 'Entrée en caisse',
    resultat: (gain: boolean, m: string) => `${gain ? 'Gain' : 'Perte'} de change : ${m}`,
    detail: (sortie: string, entree: string) => `Sortie ${sortie} · entrée ${entree} (contrevaleurs au taux du jour)`,
    bouton: 'Enregistrer le change',
  },

  cloture: {
    faite: 'Caisse clôturée.',
    titre: 'Clôture de caisse — comptage par devise',
    theorique: 'Théorique',
    compte: 'Compte',
    ecart: (m: string) => `Ecart de ${m} — motif obligatoire`,
    bouton: 'Clôturer',
  },

  taux: {
    misAJour: 'Taux mis à jour — les mouvements passes ne sont pas modifies.',
    titre: 'Taux appliqués en agence',
    texteAvant: 'Le taux appliqué en agence n\'est pas celui de la banque. Chaque taux est historise avec sa date d\'effet, et ',
    texteGras: 'les mouvements déjà enregistrés gardent leur contrevaleur d\'origine',
    texteApres: '.',
    bouton: 'Enregistrer les taux',
  },
};
